import type { CollectionEntry, RiftboundCard, RiftboundFilter } from '../types';
import { queryEqualsOfficialAlternate } from '../../shared/alternateCardNames.js';
import { searchQueryForNameMatch } from './cardPrintSuffix';
import { fuzzyNameMatches, nameHasWordPrefix } from './fuzzyName';
import type { DeckCardRef, DeckDraft } from '../types/deck';
import { ALL_SET_LANGUAGES, DEFAULT_GRID_SORT, SET_LANGUAGE_LABELS, SETS, type SetLanguage } from '../constants';
import { setCodeForCollectorTotal, setCollectorTotal } from '../../shared/setReleaseCalendar.js';
import { cardTypeSortRank } from '../../shared/cardTypeSort.js';
import {
  cardCollectorNumber,
  collectorNumbersEqual,
  formatCardNumber as formatSharedCardNumber,
  normalizeCardNumber as normalizeSharedCardNumber,
} from '../../shared/cardCollectorNumber.js';
import { cardMatchesSearchVariants, cardMatchesSetFilter, cardOriginSet, isPromoCard, isPromoSetCode } from './cardPrintVariant';
import { printsPromoMark } from './cardRarity';
import { cardMatchesTypeFilter } from './cardTypes';

/** Release-order rank for each set code (lower = older). Unknown codes sort last. */
const SET_RELEASE_RANK = Object.fromEntries(SETS.map((s, i) => [s.code, i]));

/** Collection grid filter: show all owned, only playset (3+), or only cards still below 3 copies. */
export type CollectionPlaysetFilter = 'all' | 'atLeast3' | 'below3';

export function buildCardSearchParams(
  filter: RiftboundFilter,
  searchTerm: string,
  sort: string,
  page = 1,
  pageSize = 40
): URLSearchParams {
  const params = new URLSearchParams();
  if (searchTerm.trim()) params.set('q', searchTerm.trim());
  if (filter.set.length) params.set('set', filter.set.join(','));
  if (filter.cardType.length) params.set('type', filter.cardType.join(','));
  if (filter.supertype.length) params.set('supertype', filter.supertype.join(','));
  if (filter.region.length) params.set('region', filter.region.join(','));
  if (filter.champion.length) params.set('champion', filter.champion.join(','));
  if (filter.keyword.length) params.set('keyword', filter.keyword.join(','));
  if (filter.subtype.length) params.set('subtype', filter.subtype.join(','));
  if (filter.rarity.length) params.set('rarity', filter.rarity.join(','));
  if (filter.domain.length) params.set('domain', filter.domain.join(','));
  if (filter.artist.length) params.set('artist', filter.artist.join(','));
  if (filter.variant?.length) params.set('variant', filter.variant.join(','));
  if (filter.cost.length) params.set('cost', filter.cost.join(','));
  if (filter.might.length) params.set('might', filter.might.join(','));
  if (filter.power?.length) params.set('power', filter.power.join(','));
  if (sort && sort !== DEFAULT_GRID_SORT) params.set('sort', sort);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return params;
}

export function hasActiveFilter(filter: RiftboundFilter, searchTerm: string): boolean {
  if (searchTerm.trim()) return true;
  return countActiveFilters(filter) > 0;
}

/** Number of discrete filter facets currently applied (for badges / sheet titles). */
export function countActiveFilters(
  filter: RiftboundFilter,
  options?: { ignoreSet?: boolean },
): number {
  let n = 0;
  // Tolerate partial filter objects (e.g. legacy state) — treat missing arrays as empty.
  if ((filter.cardType?.length ?? 0) > 0) n++;
  if ((filter.supertype?.length ?? 0) > 0) n++;
  if ((filter.region?.length ?? 0) > 0) n++;
  if ((filter.champion?.length ?? 0) > 0) n++;
  if ((filter.keyword?.length ?? 0) > 0) n++;
  if ((filter.subtype?.length ?? 0) > 0) n++;
  if (!options?.ignoreSet && (filter.set?.length ?? 0) > 0) n++;
  if ((filter.rarity?.length ?? 0) > 0) n++;
  if ((filter.domain?.length ?? 0) > 0) n++;
  if ((filter.artist?.length ?? 0) > 0) n++;
  if ((filter.variant?.length ?? 0) > 0) n++;
  if ((filter.cost?.length ?? 0) > 0) n++;
  if ((filter.might?.length ?? 0) > 0) n++;
  if ((filter.power?.length ?? 0) > 0) n++;
  if (filter.ownedOnly) n++;
  if (filter.includePreviewed === false) n++;
  if (filter.priceMin != null || filter.priceMax != null) n++;
  return n;
}

export function cardKey(card: RiftboundCard): string {
  return card.id || card.name;
}

/** First occurrence wins; preserves order. Drops later rows with the same `id`. */
export function dedupeCardsById(cards: RiftboundCard[]): RiftboundCard[] {
  const seen = new Set<string>();
  const out: RiftboundCard[] = [];
  for (const card of cards) {
    const id = card.id;
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(card);
  }
  return out;
}

// ── Slug helpers ─────────────────────────────────────────────────────────────

/**
 * Converts a card name into a URL-safe slug segment.
 * "Arachnoid Sentry" → "arachnoid-sentry"
 */
export function slugifyCardName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')   // strip non-alphanumeric (except spaces/hyphens)
    .trim()
    .replace(/[\s]+/g, '-')          // spaces → hyphens
    .replace(/-{2,}/g, '-');         // collapse double hyphens
}

/**
 * Sets whose face prints the language after the collector line (`· EN`).
 * That starts at Vendetta and continues from Radiance onward.
 * Every promo printing (OPP, PR, JDG) adds `· P` after the total, on any set.
 */
const SETS_PRINT_EXTENDED_COLLECTOR = new Set(['VEN', 'RAD', 'LGC', 'PG2', 'REC', 'S08', 'S09']);

/**
 * Set code printed on the card. Promo catalogs show the parent set.
 * When `originSet` was never stored, the id's set size is enough
 * (`jdg-087-219` is Unleashed, `opp-124-221` is Spiritforged).
 */
function printedSetCode(
  card: Pick<RiftboundCard, 'setCode' | 'originSet' | 'raw' | 'riftboundId'>,
): string {
  const catalog = String(card.setCode || '').trim().toUpperCase();
  if (!isPromoSetCode(catalog)) return catalog;
  const origin = cardOriginSet(card);
  if (origin) return origin.toUpperCase();
  const parts = String(card.riftboundId || '').split('-');
  const total = [...parts].reverse().find((part) => /^\d+$/.test(part));
  return (total && setCodeForCollectorTotal(total)) || catalog;
}

/**
 * Printed set size. Known sets always win over a trailing riftbound segment,
 * so `VEN-R02b` and a promo id that invented `/298` both read as `/166`.
 * Sets with no published size fall back to that segment, then omit `/total`.
 */
function printedCollectorTotal(set: string, riftboundId?: string | null): string | null {
  const known = setCollectorTotal(set);
  if (known) return String(known);
  if (!riftboundId) return null;
  const parts = riftboundId.split('-');
  const last = parts[parts.length - 1];
  if (parts.length >= 3 && /^\d+$/.test(last)) return last;
  return null;
}

/**
 * Collector token for the printed line. Numeric collectors are zero-padded.
 * A rune id that baked the set size in (`r06b-167`) keeps `r06b`.
 */
function displayCollectorToken(numRaw: string, total: string | null): string {
  const embedded = numRaw.match(/^([rt]\d+[a-z]*)-(\d+)$/i);
  const token = embedded && total && embedded[2] === total ? embedded[1] : numRaw;
  const numMatch = token.match(/^(\d+)([a-z]*)$/i);
  if (numMatch) return numMatch[1].padStart(3, '0') + numMatch[2].toLowerCase();
  return token.toUpperCase();
}

type CollectorLineCard = Pick<
  RiftboundCard,
  'setCode' | 'number' | 'riftboundId' | 'originSet' | 'raw' | 'cardType'
> & Partial<Pick<RiftboundCard, 'rarity' | 'language'>>;

/**
 * Code printed after the collector total. Simplified Chinese faces print `SC`,
 * not the BCP-47 `zh` we store on the card.
 */
const PRINTED_LANGUAGE_CODE: Partial<Record<SetLanguage, string>> = {
  en: 'EN',
  zh: 'SC',
};

function printedLanguageMark(language: string | undefined): string {
  const code = (language || 'en').toLowerCase();
  return PRINTED_LANGUAGE_CODE[code as SetLanguage] ?? code.toUpperCase();
}

/**
 * Collector token for the printed line. A language kept on the id so a Chinese
 * judge face stays a different printing from the English one (`086zh`) is not
 * part of the number on the card.
 */
function collectorNumberForPrint(
  card: Pick<RiftboundCard, 'number' | 'riftboundId' | 'language'>,
): string {
  const numRaw = cardCollectorNumber(card);
  const lang = String(card.language || '').trim().toLowerCase();
  if (lang && lang !== 'en' && numRaw.toLowerCase().endsWith(lang)) {
    return numRaw.slice(0, -lang.length);
  }
  return numRaw;
}

/**
 * Collector line as printed in the card's bottom corner.
 * `RAD · 139/167 · EN`, `VEN · R04b/166 · P · EN`, `SFD · 124/221 · P`.
 * Every promo printing adds `P` after the total.
 */
export function formatPrintedCollectorLine(card: CollectorLineCard): string | null {
  const set = printedSetCode(card);
  const numRaw = collectorNumberForPrint(card);
  if (!set || !numRaw) return null;

  const total = printedCollectorTotal(set, card.riftboundId);
  const num = displayCollectorToken(numRaw, total);

  const parts = [`${set} · ${total ? `${num}/${total}` : num}`];
  if (printsPromoMark(card)) parts.push('P');
  if (SETS_PRINT_EXTENDED_COLLECTOR.has(set)) parts.push(printedLanguageMark(card.language));
  return parts.join(' · ');
}

/**
 * Collector number in card details. Same line as {@link formatPrintedCollectorLine}
 * without the set prefix, which already has its own row.
 */
export function formatCollectorDisplay(card: CollectorLineCard): string | null {
  const line = formatPrintedCollectorLine(card);
  if (!line) return null;
  const prefix = `${printedSetCode(card)} · `;
  return line.startsWith(prefix) ? line.slice(prefix.length) : line;
}

/**
 * Plain-language reading of {@link formatPrintedCollectorLine}.
 * `VEN · 041a/166 · P · EN` → "Vendetta, card 41 of 166, alternate art, promo, English".
 */
export function describePrintedCollectorLine(
  card: Pick<
    RiftboundCard,
    'setCode' | 'setName' | 'cardType' | 'number' | 'riftboundId' | 'originSet' | 'raw' | 'language'
  >,
): string | null {
  const line = formatPrintedCollectorLine(card);
  if (!line) return null;

  const promo = isPromoCard(card);
  const printedSet = printedSetCode(card);
  const setName =
    (!promo && card.setName?.trim()) ||
    SETS.find((set) => set.code.toUpperCase() === printedSet)?.name ||
    printedSet;

  const totalText = printedCollectorTotal(printedSet, card.riftboundId);
  const total = totalText != null ? Number(totalText) : null;
  const token = displayCollectorToken(collectorNumberForPrint(card), totalText);
  const numMatch = token.match(/^(\d+)([a-z]*)$/i);
  const collector = numMatch ? String(Number(numMatch[1])) : token;
  const suffix = (numMatch?.[2] ?? '').toLowerCase();

  const traits: string[] = [];
  if (suffix.includes('a')) traits.push('alternate art');
  if (suffix.includes('s')) traits.push('signed');
  if (total != null && numMatch && Number(numMatch[1]) > total) traits.push('overnumbered');
  if (line.includes(' · P')) traits.push('promo');
  if (traits.length === 0) traits.push('standard');

  const printedCode = line.match(/ · ([A-Z]{2})$/)?.[1]?.toUpperCase();
  const languageKey = printedCode
    ? ALL_SET_LANGUAGES.find((key) => PRINTED_LANGUAGE_CODE[key] === printedCode)
    : undefined;
  const language = languageKey ? SET_LANGUAGE_LABELS[languageKey] : null;

  const numberPhrase = total != null ? `card ${collector} of ${total}` : `card ${collector}`;
  return [setName, numberPhrase, ...traits, language].filter(Boolean).join(', ');
}

/**
 * Formats a collector number for use in a URL slug.
 * Numeric numbers are zero-padded to 3 digits; non-numeric kept as-is (lowercased).
 * "42" → "042", "42a" → "42a"
 */
export function formatCardNumber(num: string | undefined): string {
  return formatSharedCardNumber(num);
}

/**
 * Returns the `{setCode}/{number}-{name-slug}` segment for a card URL.
 * e.g. "ogn/042-arachnoid-sentry"
 *
 * Prefers the riftbound_id collector segment so alt-art prints (`051a`) get a
 * distinct slug from the base print even when `number` is the shared integer.
 *
 * Use as: `/cards/${cardSlugPath(card)}`
 */
export function cardSlugPath(
  card: Pick<RiftboundCard, 'setCode' | 'number' | 'name' | 'riftboundId'>,
): string {
  const set = (card.setCode || 'unknown').toLowerCase();
  const num = formatCardNumber(cardCollectorNumber(card) || card.number);
  const name = slugifyCardName(card.name || 'card');
  return `${set}/${num}-${name}`;
}

/**
 * Collector prefixes for a `/cards/{set}/{slug}` tail, longest first.
 * `r02b-167-calm-rune` yields `r02b-167-calm`, `r02b-167`, then `r02b`.
 */
export function cardPathCollectorCandidates(cardSlug: string): string[] {
  const parts = cardSlug.split('-').filter(Boolean);
  const candidates: string[] = [];
  for (let i = parts.length - 1; i >= 1; i -= 1) {
    candidates.push(parts.slice(0, i).join('-'));
  }
  return candidates;
}

/**
 * True when `card` is the printing named by `/cards/{setCode}/{cardSlug}`.
 * An exact slug wins. A shorter collector (`r02b`) must not take a path whose
 * next segment is a print run (`r02b-167-…`); that URL belongs to the longer
 * collector (`r02b-167`).
 */
export function cardMatchesCollectorPath(
  card: Pick<RiftboundCard, 'setCode' | 'number' | 'name' | 'riftboundId'>,
  setCode: string | undefined,
  cardSlug: string | undefined,
): boolean {
  if (!setCode || !cardSlug) return false;
  if (card.setCode?.toLowerCase() !== setCode.toLowerCase()) return false;

  const full = `${setCode}/${cardSlug}`.toLowerCase();
  const ownPath = card.setCode && (cardCollectorNumber(card) || card.number)
    ? cardSlugPath(card).toLowerCase()
    : '';
  if (ownPath && ownPath === full) return true;

  const collector = String(cardCollectorNumber(card) || card.number || '').toLowerCase();
  const slug = cardSlug.toLowerCase();
  // The path already includes this printing's full collector (`015-167-…`).
  // The rest must be that card's name, so a sibling print run
  // (`015-167-champion-…`) cannot claim it.
  if (collector && (slug === collector || slug.startsWith(`${collector}-`))) {
    if (!ownPath) return true;
    const ownSlug = ownPath.slice(ownPath.indexOf('/') + 1);
    const namePart = slug.slice(collector.length).replace(/^-/, '');
    const ownName = ownSlug.slice(collector.length).replace(/^-/, '');
    if (!namePart || !ownName) return true;
    return ownName === namePart
      || ownName.startsWith(`${namePart}-`)
      || namePart.startsWith(`${ownName}-`);
  }

  const [pathCollector, second] = cardSlug.split('-');
  if (!pathCollector || !collectorNumbersEqual(cardCollectorNumber(card), pathCollector)) return false;
  if (second && /^\d+$/.test(second)) {
    const own = normalizeSharedCardNumber(String(cardCollectorNumber(card) || card.number || ''));
    const extended = normalizeSharedCardNumber(`${pathCollector}-${second}`);
    return own === extended;
  }
  return true;
}

/** Load the printing for a card path, trying the longest collector first. */
export async function fetchCardForCollectorPath(
  setCode: string,
  cardSlug: string,
): Promise<RiftboundCard | null> {
  for (const number of cardPathCollectorCandidates(cardSlug)) {
    try {
      const res = await fetch(
        `/api/card?set=${encodeURIComponent(setCode)}&number=${encodeURIComponent(number)}`,
      );
      if (!res.ok) continue;
      const json = (await res.json()) as RiftboundCard;
      if (json?.id && cardMatchesCollectorPath(json, setCode, cardSlug)) return json;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Strips leading zeros from a collector number string for DB comparison.
 * "042" → "42", "001" → "1", "051a" → "51a"
 */
export function normalizeCardNumber(num: string): string {
  return normalizeSharedCardNumber(num);
}

/** Re-export: prefer riftbound_id collector segment over bare `number`. */
export { cardCollectorNumber } from '../../shared/cardCollectorNumber.js';

export function cardMatchesSidebarFilters(
  card: RiftboundCard,
  filter: RiftboundFilter,
  searchTerm: string
): boolean {
  if (searchTerm.trim()) {
    const q = searchTerm.toLowerCase();
    const qName = searchQueryForNameMatch(searchTerm).toLowerCase();
    const name = (card.name || '').toLowerCase();
    const text = (card.text || '').toLowerCase();
    const zh = card.locales?.zh;
    const ko = card.locales?.ko;
    const zhText = `${zh?.text || ''} ${zh?.flavor || ''}`.toLowerCase();
    const koText = `${ko?.text || ''} ${ko?.flavor || ''}`.toLowerCase();
    const tags = (card.tags || []).join(' ').toLowerCase();
    const artist = (card.artist || '').toLowerCase();
    const nameMatches =
      nameHasWordPrefix(name, q) ||
      nameHasWordPrefix(name, qName) ||
      queryEqualsOfficialAlternate(card, q) ||
      queryEqualsOfficialAlternate(card, qName) ||
      fuzzyNameMatches(name, qName) ||
      fuzzyNameMatches(name, q);
    if (!nameMatches && !text.includes(q) && !zhText.includes(q) && !koText.includes(q) && !tags.includes(q) && !artist.includes(q)) return false;
  }

  if (filter.set?.length && !cardMatchesSetFilter(card, filter.set)) return false;
  if (filter.variant?.length && !cardMatchesSearchVariants(card, filter.variant)) return false;
  if (filter.cardType?.length && !cardMatchesTypeFilter(card, filter.cardType)) return false;
  if (filter.supertype?.length && (!card.supertype || !filter.supertype.includes(card.supertype))) return false;
  if (filter.region?.length && (!card.region || !filter.region.includes(card.region))) return false;
  if (filter.champion?.length && (!card.champion || !filter.champion.includes(card.champion))) return false;
  if (filter.rarity?.length && (!card.rarity || !filter.rarity.includes(card.rarity))) return false;
  if (filter.domain?.length && !card.neutral) {
    const dom = card.domain ?? [];
    if (!filter.domain.some((d) => dom.includes(d))) return false;
  }
  if (filter.keyword?.length) {
    const kw = card.keywords || [];
    if (!filter.keyword.some((k) => kw.includes(k))) return false;
  }
  if (filter.subtype?.length) {
    const t = card.tags || [];
    if (!filter.subtype.some((s) => t.includes(s))) return false;
  }
  if (filter.artist?.length && (!card.artist || !filter.artist.includes(card.artist))) return false;
  if (filter.cost?.length && (card.cost == null || !filter.cost.includes(card.cost))) return false;
  if (filter.might?.length && (card.might == null || !filter.might.includes(card.might))) return false;
  if (filter.power?.length && (card.power == null || !filter.power.includes(card.power))) return false;
  if (!filter.includePreviewed && card.previewed) return false;

  return true;
}

export function collectionPlaysetMatches(quantity: number, playset: CollectionPlaysetFilter): boolean {
  if (playset === 'atLeast3') return quantity >= 3;
  if (playset === 'below3') return quantity > 0 && quantity < 3;
  return quantity > 0;
}

/** Filter loaded collection cards by sidebar filters, search, and 3× playset rule. */
export function filterDisplayedCollection(
  cards: RiftboundCard[],
  entries: Record<string, CollectionEntry>,
  filter: RiftboundFilter,
  searchTerm: string,
  playset: CollectionPlaysetFilter
): RiftboundCard[] {
  return cards.filter((card) => {
    const entry = entries[card.id];
    const q = entry?.quantity ?? 0;
    if (!collectionPlaysetMatches(q, playset)) return false;
    return cardMatchesSidebarFilters(card, filter, searchTerm);
  });
}

/** Number of copies needed for a playset goal: 1 = at least one copy, 3 = full playset. */
export type CollectionMissingTarget = 1 | 3;

export interface DeckTrayLineRequest {
  cardId: string;
  qty: number;
}

/** Sum quantities when the same cardId appears in multiple zones (keeps metadata from first occurrence). */
export function mergeDeckCardRefsById(refs: DeckCardRef[]): DeckCardRef[] {
  const byId = new Map<string, DeckCardRef>();
  for (const ref of refs) {
    const prev = byId.get(ref.cardId);
    if (!prev) byId.set(ref.cardId, ref);
    else byId.set(ref.cardId, { ...prev, qty: prev.qty + ref.qty });
  }
  return [...byId.values()];
}

/**
 * All deck slots backed by printable card refs (chosen champion, legend, Neeko's
 * chosen legends, battlefields, library, sideboard). Rune piles are domains, not
 * individual cards — omitted here.
 */
export function deckCardRefsAllPrintZones(
  deck: Pick<
    DeckDraft,
    'mainDeck' | 'sideboard' | 'legend' | 'champion' | 'battlefields'
  > & Partial<Pick<DeckDraft, 'chosenLegends'>>
): DeckCardRef[] {
  const refs: DeckCardRef[] = [
    ...deck.mainDeck,
    ...deck.sideboard,
    ...deck.battlefields,
    ...(deck.legend ? [deck.legend] : []),
    ...(deck.chosenLegends ?? []),
    ...(deck.champion ? [deck.champion] : []),
  ];
  return mergeDeckCardRefsById(refs);
}

/** Deck line items for proxy tray; when missingOnly, qty is shortfall vs collection owned count. */
export function deckMainDeckTrayRequests(
  lines: DeckCardRef[],
  entries: Record<string, CollectionEntry>,
  missingOnly: boolean
): DeckTrayLineRequest[] {
  const out: DeckTrayLineRequest[] = [];
  for (const entry of lines) {
    const needed = entry.qty;
    if (missingOnly) {
      const owned = entries[entry.cardId]?.quantity ?? 0;
      const shortfall = needed - owned;
      if (shortfall <= 0) continue;
      out.push({ cardId: entry.cardId, qty: shortfall });
    } else {
      out.push({ cardId: entry.cardId, qty: needed });
    }
  }
  return out;
}

/** Returns catalog cards where owned quantity is strictly below the target (includes zero-owned). */
export function filterCatalogMissingTarget(
  catalogCards: RiftboundCard[],
  entries: Record<string, CollectionEntry>,
  target: CollectionMissingTarget
): RiftboundCard[] {
  return catalogCards.filter(
    (card) => (entries[card.id]?.quantity ?? 0) < target
  );
}

export type SortCardsByOptionContext = {
  /** Collection entries — required for `scanned-desc` / `updated-desc` recency. */
  entries?: Record<string, CollectionEntry>;
  /** Max scan-session `at` per cardId from the last-scan stash. */
  scanAtByCardId?: Record<string, number>;
  /** Unit market price per cardId — required for `price-asc` / `price-desc`. */
  unitPriceByCardId?: Record<string, number>;
};

/** Fields used by catalog / deck-list sorts. `id` is only needed for recency sorts. */
export type SortableCardFields = {
  id?: string;
  name?: string;
  setCode?: string;
  cardType?: string;
  cost?: number;
  might?: number;
  number?: string;
};

function compareByName(a: SortableCardFields, b: SortableCardFields): number {
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
}

function compareByCollectorNumber(a: SortableCardFields, b: SortableCardFields): number {
  const setRankA = SET_RELEASE_RANK[a.setCode ?? ''] ?? 9999;
  const setRankB = SET_RELEASE_RANK[b.setCode ?? ''] ?? 9999;
  return (
    setRankA - setRankB ||
    parseInt(a.number || '0', 10) - parseInt(b.number || '0', 10) ||
    compareByName(a, b)
  );
}

export function sortCardsByOption<T extends SortableCardFields>(
  cards: T[],
  sort: string,
  context?: SortCardsByOptionContext,
): T[] {
  const copy = [...cards];
  switch (sort) {
    case 'scanned-desc': {
      const scanAt = context?.scanAtByCardId ?? {};
      const entries = context?.entries ?? {};
      return copy.sort((a, b) => {
        const scanA = scanAt[a.id ?? ''] ?? 0;
        const scanB = scanAt[b.id ?? ''] ?? 0;
        if (scanA !== scanB) return scanB - scanA;
        const updatedA = entries[a.id ?? '']?.updatedAt ?? 0;
        const updatedB = entries[b.id ?? '']?.updatedAt ?? 0;
        if (updatedA !== updatedB) return updatedB - updatedA;
        return compareByCollectorNumber(a, b);
      });
    }
    case 'name-desc':
      return copy.sort((a, b) =>
        (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' })
      );
    case 'cost-asc':
      return copy.sort(
        (a, b) =>
          (a.cost ?? 999) - (b.cost ?? 999) ||
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    case 'cost-desc':
      return copy.sort(
        (a, b) =>
          (b.cost ?? -1) - (a.cost ?? -1) ||
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    case 'type-cost-asc':
    case 'type-cost-desc': {
      const dir = sort === 'type-cost-desc' ? -1 : 1;
      return copy.sort((a, b) => {
        const byType = cardTypeSortRank(a.cardType) - cardTypeSortRank(b.cardType);
        if (byType !== 0) return byType;
        const byCost = dir === 1
          ? (a.cost ?? 999) - (b.cost ?? 999)
          : (b.cost ?? -1) - (a.cost ?? -1);
        if (byCost !== 0) return byCost;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });
    }
    case 'might-asc':
      return copy.sort(
        (a, b) =>
          (a.might ?? 999) - (b.might ?? 999) ||
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    case 'might-desc':
      return copy.sort(
        (a, b) =>
          (b.might ?? -1) - (a.might ?? -1) ||
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    case 'set-newest':
      return copy.sort(
        (a, b) =>
          (b.setCode || '').localeCompare(a.setCode || '') ||
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    case 'number-asc':
      return copy.sort(compareByCollectorNumber);
    case 'price-asc':
    case 'price-desc': {
      const prices = context?.unitPriceByCardId ?? {};
      const missing = sort === 'price-asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      return copy.sort((a, b) => {
        const pa = prices[a.id ?? ''] ?? missing;
        const pb = prices[b.id ?? ''] ?? missing;
        if (pa !== pb) return sort === 'price-asc' ? pa - pb : pb - pa;
        return compareByCollectorNumber(a, b);
      });
    }
    default:
      return copy.sort(compareByName);
  }
}

/** Keep only cards that appear in the last-scan session (by cardId). */
export function filterCardsByJustScanned(
  cards: RiftboundCard[],
  scanCardIds: ReadonlySet<string> | readonly string[],
): RiftboundCard[] {
  const idSet = scanCardIds instanceof Set ? scanCardIds : new Set(scanCardIds);
  if (idSet.size === 0) return [];
  return cards.filter((card) => idSet.has(card.id));
}
